import React from 'react';
import {
  Chip, Link, Box, Switch, TextField, IconButton, Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as SaveIcon,
  Close as CancelIcon,
} from '@mui/icons-material';

export function createScraperOverviewColumns({
  editingRowId,
  editedValues,
  saving,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onUpdateField,
}) {
  return [
    {
      accessorKey: '_id',
      header: 'ID',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'site_name',
      header: 'Site Name',
      size: 300,
      Cell: ({ row }) => {
        const name = row.original.site_name;
        const url = row.original.site;
        if (!name) return '';
        if (!url) return name;
        return (
          <Link href={url} target="_blank" rel="noopener noreferrer" underline="hover">
            {name}
          </Link>
        );
      },
    },
    {
      accessorKey: 'scraper_type',
      header: 'Type',
      size: 180,
      Cell: ({ cell }) => {
        const types = cell.getValue();
        if (!Array.isArray(types)) return '';
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {types.map((t) => (
              <Chip key={t} label={t} size="small" color="default" variant="outlined" />
            ))}
          </Box>
        );
      },
      filterFn: (row, _columnId, filterValue) => {
        const types = row.getValue('scraper_type');
        if (!filterValue) return true;
        return types?.some((t) => t.toLowerCase().includes(filterValue.toLowerCase()));
      },
    },
    {
      accessorKey: 'active',
      header: 'Status',
      size: 120,
      Cell: ({ row }) => {
        const isEditing = editingRowId === row.original._id;
        if (isEditing) {
          const checked = editedValues.active ?? row.original.active;
          return (
            <Switch
              size="small"
              checked={Boolean(checked)}
              onChange={(e) => onUpdateField('active', e.target.checked)}
              color={checked ? 'success' : 'default'}
            />
          );
        }
        const active = row.original.active;
        return (
          <Chip
            label={active ? 'Active' : 'Inactive'}
            size="small"
            color={active ? 'success' : 'default'}
          />
        );
      },
      filterFn: (row, _columnId, filterValue) => {
        if (!filterValue) return true;
        const active = row.getValue('active');
        const label = active ? 'active' : 'inactive';
        return label.includes(filterValue.toLowerCase());
      },
    },
    {
      accessorKey: 'interval_minutes',
      header: 'Interval (min)',
      size: 150,
      Cell: ({ row }) => {
        const isEditing = editingRowId === row.original._id;
        if (isEditing) {
          const value = editedValues.interval_minutes ?? row.original.interval_minutes;
          return (
            <TextField
              size="small"
              variant="standard"
              type="number"
              value={value}
              onChange={(e) => onUpdateField('interval_minutes', parseInt(e.target.value, 10) || '')}
              inputProps={{ min: 1 }}
              sx={{
                width: 80,
                '& .MuiInput-input': { fontSize: '0.875rem', padding: '2px 0' },
              }}
            />
          );
        }
        return row.original.interval_minutes;
      },
    },
    {
      accessorKey: 'last_notification',
      header: 'Last Notification',
      size: 180,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (!value) return '';
        return new Date(value).toLocaleString();
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 450,
      muiTableBodyCellProps: {
        sx: { whiteSpace: 'normal', wordBreak: 'break-word' },
      },
    },
    {
      id: 'actions',
      header: '',
      size: 100,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const isEditing = editingRowId === row.original._id;
        if (isEditing) {
          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Save">
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => onSaveEdit(row.original._id)}
                    disabled={saving}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={onCancelEdit} disabled={saving}>
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }
        return (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onStartEdit(row.original)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];
}
